import { ReactElement } from 'react'
import { useWatch } from 'react-hook-form'
import { Box, Grid2, Stack } from '@mui/material'
import { InfoTooltip } from '@ui/feedback'
import { MillimeterField, UnitConvertedField } from '@ui/forms'
import {
  CheckSettingsFormFieldsCommonProps,
  compactCheckSettingsFormSx,
  getCheckSettingsGridSpacing,
  getCheckSettingsMaxWidth,
  getCheckSettingsStackSpacing,
} from '../checkSettingsFormFieldLayout'
import { DefaultValues } from '../types'

const MEMBER_SETTINGS_ROOT = 'memberCheckSettingsSelectedElement' as const

interface Props extends CheckSettingsFormFieldsCommonProps {
  memberCheckSettingsSelectedPath?: string
}

const SteelBasePlateConfigFormFields = ({
  disabled,
  compact,
  memberCheckSettingsSelectedPath = MEMBER_SETTINGS_ROOT,
}: Props): ReactElement | null => {
  const memberCheckSettingsSelectedElement: SettingsOnMember = useWatch<DefaultValues>({
    name: memberCheckSettingsSelectedPath as 'memberCheckSettingsSelectedElement',
  })

  if (!('steel_support_config' in memberCheckSettingsSelectedElement))
    throw new Error('Steel support conifg not available')

  return (
    <Box
      data-cy="steel-base-plate-fields-container"
      maxWidth={getCheckSettingsMaxWidth(compact)}
      sx={compact ? compactCheckSettingsFormSx : undefined}
    >
      <Stack direction="column" spacing={getCheckSettingsStackSpacing(compact, 2)}>
        <Grid2 container spacing={getCheckSettingsGridSpacing(compact)}>
          <Grid2 size={6}>
            <MillimeterField
              name={`${memberCheckSettingsSelectedPath}.steel_support_config.base_plate_geometry.thickness`}
              label="Dicke"
              numeralFormatter="0" // 0 decimals
              disabled={disabled}
            />
          </Grid2>
          <Grid2 size={6}>
            <MillimeterField
              name={`${memberCheckSettingsSelectedPath}.steel_support_config.base_plate_geometry.length`}
              label="Länge (parallel zum Steg)"
              numeralFormatter="0" // 0 decimals
              disabled={disabled}
            />
          </Grid2>
          <Grid2 size={6}>
            <MillimeterField
              name={`${memberCheckSettingsSelectedPath}.steel_support_config.base_plate_geometry.width`}
              label="Breite (parallel zum Flansch)"
              numeralFormatter="0" // 0 decimals
              disabled={disabled}
            />
          </Grid2>
          <Grid2 size={6}>
            <Grid2 container spacing={getCheckSettingsGridSpacing(compact)}>
              <Grid2 size={11}>
                <UnitConvertedField
                  name={`${memberCheckSettingsSelectedPath}.steel_support_config.design_support_material_strength`}
                  conversionFactor={1000000}
                  label="Design-Widerstand des darunterliegenden Materials"
                  type="number"
                  unitLabel="N/mm²"
                  disabled={disabled}
                />
              </Grid2>
              <Grid2 size={1}>
                <InfoTooltip
                  text="für Holz: der Design-Widerstand muss bereits mit allen Beiwerten wie γ_M und k_mod multipliziert werden"
                  secondLineText="für Beton/Mörtel: der Design-Widerstand muss mit dem Faktor 2/3 (β_j) multipliziert werden (s. EC3-1-8 6.2.5 (2010-12))"
                />
              </Grid2>
            </Grid2>
          </Grid2>
          <Grid2 size={6}>
            <MillimeterField
              name={`${memberCheckSettingsSelectedPath}.steel_support_config.weld_thickness`}
              label="Schweißnahtdicke"
              numeralFormatter="0" // 0 decimals
              disabled={disabled}
            />
          </Grid2>
        </Grid2>
      </Stack>
    </Box>
  )
}

export default SteelBasePlateConfigFormFields
